import { useLocation } from 'react-router-dom';
import { LinkProps } from '../..';
import { getInternalLink } from '../../utils';

export interface MemorizedLocationState {
  prevLocation?: ReturnType<typeof useLocation>;
}

export const useMemorizedLink = () => {
  const location = useLocation<MemorizedLocationState>();

  /**
   * Memorizes the current location and returns the link to navigate to with the memorized location in the state
   * @param to - Link to redirect to
   * @returns Link to navigate to with the memorized location in the state
   */
  const memorizeCurrentLocation = (to: LinkProps['to']) => {

    const link = getInternalLink(to, location);

    if (typeof link === 'string') {
      return {
        pathname: link,
        state: {
          prevLocation: location
        }
      };
    }

    const state = typeof link.state === 'object' ? link.state : {};

    return {
      ...link,
      state: {
        ...state,
        prevLocation: location
      }
    };
  };

  /**
   * Returns the memorized location if it exists, otherwise the default link
   * @param defaultLink Link to navigate to if there is no memorized location
   * @returns Memorized location if it exists, otherwise the default link
   */
  const getMemorizedLink = (defaultLink: LinkProps['to']) => {
    const memorizedLocation = location.state?.prevLocation;
    if (!memorizedLocation) return getInternalLink(defaultLink, location);
    return memorizedLocation;
  };

  /**
   * Persists the memorized location if present and returns the link to navigate to with the memorized location in the state
   * @param to - Link to navigate to
   * @returns Link to navigate to with the memorized location in the state if present or the default link without the memorized location
   */
  const persistMemorizedLocation = (to: LinkProps['to']) => {
    const memorizedLocation = location.state?.prevLocation;
    if (!memorizedLocation) return getInternalLink(to, location);

    const link = getInternalLink(to, location);

    if (typeof link === 'string') {
      return {
        pathname: link,
        state: {
          prevLocation: memorizedLocation
        }
      };
    }

    const state = typeof link.state === 'object' ? link.state : {};

    return {
      ...link,
      state: {
        ...state,
        prevLocation: memorizedLocation
      }
    };

  };

  return {
    memorizeCurrentLocation,
    getMemorizedLink,
    persistMemorizedLocation
  };
};